import React, { useMemo, useState } from 'react';
import { EquationChart } from './EquationChart';
import { Question, EquationData, PointData, ChartEquationData } from '../../types';
import { createAdvancedEvaluator } from '../../utils/advancedMathEvaluator';

// Advanced math expression evaluator with backward compatibility
const createEvaluator = (equation: string): ((x: number) => number) => {
  return createAdvancedEvaluator(equation);
};

export interface MathQuestionChartProps {
  /** The question object containing the mathematical content */
  question: Question;
  /** Custom range for the x-axis */
  xRange?: { min: number; max: number };
  /** Custom range for the y-axis */
  yRange?: { min: number; max: number };
  /** Whether to show equation visibility controls */
  showControls?: boolean;
  /** Whether to enable interactive features like zoom/pan */
  interactive?: boolean;
}

/**
 * MathQuestionChart Component
 *
 * Automatically detects mathematical equations in quiz questions and renders
 * interactive charts to visualize them. This enhances learning by providing
 * visual representations alongside textual questions.
 *
 * Features:
 * - Auto-detects linear equations in question text
 * - Plots multiple equations from question and answer choices
 * - Highlights correct answers when showAnswer is true
 * - Supports various question types with mathematical content
 * - Responsive design matching existing quiz components
 *
 * Use Cases:
 * 1. Linear equation questions (y = mx + c format)
 * 2. Slope and y-intercept problems
 * 3. System of equations visualization
 * 4. Parallel/perpendicular line comparisons
 * 5. Function evaluation and graphing
 *
 * @example
 * ```tsx
 * // For a question about linear equations
 * <MathQuestionChart
 *   question={questionData}
 *   showAnswer={isAnswered}
 *   xRange={{ min: -5, max: 5 }}
 * />
 * ```
 */
export const MathQuestionChart: React.FC<MathQuestionChartProps> = ({
  question,
  xRange = { min: -5, max: 5 },
  yRange,
  showControls = true,
  interactive = true,
}) => {
  // State for equation visibility control
  const [visibleEquations, setVisibleEquations] = useState<Set<string>>(new Set());
  const showAllEquations = true; // Show all equations by default
  // Enhanced color palette for better distinction with many equations
  const enhancedColors = useMemo(() => [
    '#3B82F6', '#EF4444', '#10B981', '#F59E0B', '#8B5CF6',
    '#06B6D4', '#EC4899', '#84CC16', '#F97316', '#6366F1',
    '#14B8A6', '#F43F5E', '#22C55E', '#A855F7', '#0EA5E9',
    '#EAB308', '#DC2626', '#059669', '#7C3AED', '#0891B2'
  ], []);

  // Convert stored equation data to chart-compatible format with enhanced features
  const chartEquations = useMemo(() => {
    if (!question.equationsJson || !Array.isArray(question.equationsJson)) {
      return [];
    }

    // Filter and convert only equation data (not points)
    const equations = question.equationsJson
      .filter((data): data is EquationData => {
        const hasEquation = 'equation' in data;
        return hasEquation;
      })
      .map((equationData, index) => {
        const equationId = `stored-equation-${index}`;

        // Initialize visibility if not set
        if (!visibleEquations.has(equationId) && showAllEquations) {
          setVisibleEquations(prev => {
            const newSet = new Set(prev);
            newSet.add(equationId);
            return newSet;
          });
        }

        const evaluatorFunc = createEvaluator(equationData.equation);

        const chartEquation: ChartEquationData = {
          id: equationId,
          equation: equationData.equation,
          label: equationData.label || equationData.equation,
          evaluate: evaluatorFunc,
          visible: Boolean(showAllEquations || visibleEquations.has(equationId)),
          // Use enhanced color cycling with fallback to stored color
          color: equationData.color || enhancedColors[index % enhancedColors.length],
          colorIndex: undefined,
        };

        return chartEquation;
      });

    return equations;
  }, [question.equationsJson, visibleEquations, showAllEquations, enhancedColors]);

  // Extract point data for overlay with enhanced features
  const chartPoints = useMemo(() => {
    if (!question.equationsJson || !Array.isArray(question.equationsJson)) {
      return [];
    }

    // Filter and convert only point data with enhanced styling
    return question.equationsJson
      .filter((item): item is PointData => 'type' in item && item.type === 'point')
      .map((point, index) => ({
        ...point,
        // Enhance point colors if not specified
        color: point.color || enhancedColors[index % enhancedColors.length],
      }));
  }, [question.equationsJson, enhancedColors]);

  // Render supplied points even when no equations are stored
  if (chartEquations.length === 0 && chartPoints.length === 0) {
    return <></>
  }

  return (
    <EquationChart
      equations={chartEquations}
      xRange={xRange}
      yRange={yRange}
      resolution={interactive ? 100 : 50}
      showLegend={!showControls || chartEquations.length === 0}
      showAxisLabels={true}
      points={chartPoints}
      customOptions={interactive ? {
        plugins: {
          zoom: {
            zoom: {
              wheel: {
                enabled: true,
              },
              pinch: {
                enabled: true
              },
              mode: 'xy',
            },
            pan: {
              enabled: true,
              mode: 'xy'
            }
          }
        }
      } : undefined}
    />
  );
};

/**
 * Enhanced hook to detect and analyze math visualization data
 *
 * @param question The question to analyze
 * @returns Object with visualization info and capabilities
 */
export const useHasMathVisualization = (question: Question) => {
  return useMemo(() => {
    if (!question.equationsJson || !Array.isArray(question.equationsJson) || question.equationsJson.length === 0) {
      return {
        hasVisualization: false,
        equationCount: 0,
        pointCount: 0,
        hasMultipleEquations: false,
        recommendsControls: false,
        recommendsGroups: false,
      };
    }

    const equations = question.equationsJson.filter(item => 'equation' in item);
    const points = question.equationsJson.filter(item => 'type' in item && item.type === 'point');

    return {
      hasVisualization: true,
      equationCount: equations.length,
      pointCount: points.length,
      hasMultipleEquations: equations.length > 1,
      recommendsControls: equations.length > 2,
      recommendsGroups: equations.length > 5,
    };
  }, [question.equationsJson]);
};